import { Database } from '@repo/types';
import { IFilterState } from '@web/types/business-details';
import { IServiceBE, IServiceForm } from '@web/types/services';
import { handleOtherCategoryId } from '@web/utils/handleOtherCategory';
import { supabaseClient } from '@web/utils/supabase-client';
import { v4 as uuidv4 } from 'uuid';

class ServiceLogic {
  public async getServiceTypeAndCategory() {
    const { data, error } = await supabaseClient
      .from('service_type')
      .select('*, service_category(*)');
    if (error) {
      console.error('error', error);
      throw error;
    }

    if (!data) {
      return null;
    }

    const otherCategory = {
      id: 0,
      category_name: 'อื่นๆ',
      created_at: '',
      updated_at: null,
      service_type_id: 0,
    };

    data.forEach((d) => {
      d.service_category.push(otherCategory);
    });

    return data;
  }

  public async createServiceByRpc(
    business_id: string,
    host_id: string,
    payload: IServiceForm,
  ) {
    const business_service_params: Database['public']['CompositeTypes']['business_service_params'] =
      {
        detail: payload.detail,
        pet_type: payload.pet_type,
        service_type_id: Number(payload.service_type_id),
        service_category_id: handleOtherCategoryId(
          Number(payload.service_category_id),
        ),
        service_name: payload.service_name,
        service_about: payload.service_about,
      };
    const service_variant_params: Database['public']['CompositeTypes']['service_variant_params'][] =
      payload.service_variant.map((item) => {
        return {
          price: Number(item.price),
          status: item.status as 'available' | 'unavailable',
          variant_name: item.variant_name,
        };
      });

    const newPayload = {
      business_id,
      host_id,
      business_service_params: business_service_params,
      service_variant_params: service_variant_params,
    };

    const { data, error } = await supabaseClient.rpc(
      'insert_business_service',
      newPayload,
    );
    if (error) {
      console.error('error', error);
      throw error;
    }
    return data;
  }

  public async createService(
    userId: string,
    businessId: string,
    payload: IServiceForm,
  ) {
    const { data, error } = await supabaseClient
      .from('business_service')
      .insert({
        business_id: businessId,
        host_id: userId,
        service_name: payload.service_name,
        pet_type: payload.pet_type,
        price: payload.price,
        service_type: payload.service_type,
        service_about: payload.service_about,
        status: payload.status,
        detail: payload.detail,
        service_type_id: Number(payload.service_type_id),
        service_category_id: handleOtherCategoryId(
          Number(payload.service_category_id),
        ),
      });

    if (error) {
      console.error('error', error);
      throw error;
    }

    return data;
  }

  public async updateService(
    service_id: string,
    host_id: string,
    payload: IServiceForm,
    selectedForDelete: string[],
  ) {
    const { data: buServiceRes, error: buServiceErr } = await supabaseClient
      .from('business_service')
      .update({
        service_name: payload.service_name,
        pet_type: payload.pet_type,
        price: payload.price,
        service_type: payload.service_type,
        service_about: payload.service_about,
        status: payload.status,
        detail: payload.detail,
        service_type_id: Number(payload.service_type_id),
        // TODO if using backend, need to set null for service_category_id
        service_category_id: handleOtherCategoryId(
          Number(payload.service_category_id),
        ),
      })
      .eq('id', service_id);

    const { data: buServiceVariantRes, error: buServiceVariantErr } =
      await supabaseClient.from('service_variant').upsert(
        payload.service_variant.map((item) => {
          return {
            id: item.id ?? uuidv4(),
            business_service_id: service_id,
            price: Number(item.price),
            status: item.status as 'available' | 'unavailable',
            variant_name: item.variant_name,
            host_id: host_id,
          };
        }),
        {
          onConflict: 'id',
        },
      );
    // delete selected service variant
    if (selectedForDelete.length > 0) {
      const { error: buServiceVariantDelErr } = await supabaseClient
        .from('service_variant')
        .delete()
        .in('id', selectedForDelete);
      if (buServiceVariantDelErr) {
        console.error('error', buServiceVariantDelErr);
        throw buServiceVariantDelErr;
      }
    }
    if (buServiceErr || buServiceVariantErr) {
      console.error('error', buServiceErr);
      throw buServiceErr;
    }

    return { buServiceRes, buServiceVariantRes };
  }

  public async getServiceByFilter(
    filter: IFilterState,
    buId: string,
  ): Promise<IServiceBE[]> {
    let query = supabaseClient
      .from('business_service')
      .select('*, service_type(*), service_category(*), service_variant(*)')
      .eq('business_id', buId)
      .eq('pet_type', filter.selectedPetType);

    if (filter.selectedServiceType != null && filter.selectedServiceType != 0) {
      query = query.eq('service_type_id', filter.selectedServiceType);
    }

    // case category อื่นๆ
    if (filter.isOtherServiceCategory) {
      query = query.is('service_category_id', null);
      // case normal category
    } else if (filter.selectedServiceCategory !== null) {
      query = query.eq('service_category_id', filter.selectedServiceCategory);
    }

    const { data, error } = await query;

    if (error) {
      console.error('error', error);
      throw error;
    }

    if (!data) {
      return [];
    }

    return data.map((d) => {
      return {
        ...d,
        price: d.price ? d.price : 0,
        status: d.status ? d.status : '',
        service_name: d.service_name ? d.service_name : '',
        service_type: d.service_type ? d.service_type : null,
        service_category: d.service_category ? d.service_category : null,
        service_type_id: d.service_type ? d.service_type.id : undefined,
        service_category_id: d.service_category
          ? d.service_category.id
          : undefined,
        service_variant: d.service_variant.map((sv) => {
          return {
            id: sv.id,
            price: sv.price,
            status: sv.status,
            variant_name: sv.variant_name,
          };
        }),
      };
    }) as IServiceBE[];
  }

  public async getServicesByBusinessId(businessId: string): Promise<{
    total: number;
    payload: IServiceBE[];
  }> {
    const { data, error, count } = await supabaseClient
      .from('business_service')
      .select('*, service_type(*), service_category(*), service_variant(*)', {
        count: 'exact',
      })
      .eq('business_id', businessId);
    if (error) {
      console.error('error', error);
      throw error;
    }

    const payload = data.map((d) => {
      return {
        ...d,
        price: d.price ? d.price : 0,
        status: d.status ? d.status : '',
        service_name: d.service_name ? d.service_name : '',
        service_type: d.service_type ? d.service_type : null,
        service_category: d.service_category ? d.service_category : null,
        service_type_id: d.service_type ? d.service_type.id : undefined,
        service_category_id: d.service_category
          ? d.service_category.id
          : undefined,
        service_variant: d.service_variant.map((sv) => {
          return {
            id: sv.id,
            price: sv.price,
            status: sv.status,
            variant_name: sv.variant_name,
          };
        }),
      };
    }) as IServiceBE[];

    return {
      total: count ?? 0,
      payload,
    };
  }

  public async deleteServiceById(serviceId: string) {
    const { data, error } = await supabaseClient
      .from('business_service')
      .delete()
      .eq('id', serviceId);
    if (error) {
      console.error('error', error);
      throw error;
    }
    return data;
  }
}

export const serviceLogic = new ServiceLogic();
